import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import { useActiveConversationContext } from "../../context/ActiveConversationContext";
import { useNewConversation } from "../../hooks/useNewConversation";
import useSNConversation from "../../hooks/useSNConversation";
import Notification from "./Notification";

interface NotificationAreaProps {
  hasMessageFromAnotherUser: boolean;
}

export default function NotificationArea({
  hasMessageFromAnotherUser,
}: NotificationAreaProps) {
  const { accounts } = useSelectedWorkspaceContext();
  const { activeConversation } = useActiveConversationContext();
  const { newConversation } = useNewConversation();
  const { isSNConversation, hasSNContract } =
    useSNConversation(activeConversation);

  let owner: string;

  if (activeConversation) {
    owner = activeConversation.owner;

    const {
      participants,
      last_message: lastMessage,
      connected: isConnected,
    } = activeConversation;

    const { is_lead: isLead } = participants[0];

    const isMessagingBlocked =
      !hasMessageFromAnotherUser && !isLead && !isConnected;

    if (isMessagingBlocked) {
      return (
        <Notification
          title="Cannot message this user"
          subtitle="You will be able to chat with this user once they respond to your message"
          variant="gray"
        />
      );
    }

    if (lastMessage.declined) {
      return (
        <Notification
          title="InMail Declined"
          subtitle="The recipient has declined your InMail, you cannot send them a message"
          variant="red"
        />
      );
    }
  } else if (newConversation) {
    owner = newConversation.owner;
  }

  if (isSNConversation && !hasSNContract) {
    return (
      <Notification
        title="You can no longer message this user."
        subtitle="Your Sales Navigator contract has expired"
        variant="gray"
      />
    );
  }

  const account = accounts.find(({ id }) => id === owner);

  if (account) {
    if (account.state === "LoggedOut") {
      return (
        <Notification
          title="Cannot message this user"
          subtitle="You will be able to chat with this user once the account is reconnected"
          variant="gray"
        />
      );
    }

    if (!account.license && account.type !== "rental") {
      return (
        <Notification
          title="Cannot message this user"
          subtitle="You will be able to chat with this user once a seat is assigned to the account"
          variant="gray"
        />
      );
    }
  }

  if (!activeConversation && newConversation) {
    return (
      <Notification
        title={`This is the very beginning of your direct message history with ${newConversation.participant.first_name}. Say hi!`}
        variant="purple"
      />
    );
  }
}
